<template>
  <div>
    <div class="panel-heading order-heading-with-stats">
      <h4 class="panel-title">{{ $t('delivery.orderDeliveryTitle') }}</h4>

      <div class="order-heading-right">
        <div class="order-toolbar-buttons">
          <a class="order-toolbar-btn order-toolbar-btn--icon-only" :title="$t('orders.filtersToggleLabel')" @click="toggleFiltersVisible">
            <i class="material-icons">{{ showFilters ? 'expand_less' : 'expand_more' }}</i>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="reloadTable">
            <i class="material-icons">cached</i>
            <span v-if="showButtonLabels">{{ $t('datatable.reloadShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('add_order_delivery')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openAddModal">
            <i class="fa fa-plus" style="color:#006b8d"></i>
            <span v-if="showButtonLabels">{{ $t('datatable.requestDeliveryButton') }}</span>
          </a>
          <a v-if="$_has_perm('change_order_delivery_status')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openBulkChangeStatusDialog">
            <i class="material-icons" style="color:#6a1b9a">autorenew</i>
            <span v-if="showButtonLabels">{{ $t('datatable.changeStatusButton') }}</span>
          </a>
          <a class="order-toolbar-display-toggle" @click="$_toggle_toolbar_button_display()">
            <i class="material-icons">{{ showButtonLabels ? 'label_off' : 'label' }}</i>
          </a>
        </div>

        <div class="order-stats-row order-stats-row--compact">
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--blue"><i class="material-icons">list_alt</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('reports.totalOrders') }}</span>
              <span class="order-stat-value">{{ pagination.total || totalResult }}</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--green"><i class="material-icons">attach_money</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('printLogs.totalAmountLabel') }}</span>
              <span class="order-stat-value">$ {{ footer ? Number(footer.total_price || 0).toFixed(2) : '0.00' }}</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--orange"><i class="material-icons">local_shipping</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('columns.deliveryPrice') }}</span>
              <span class="order-stat-value">{{ footer ? formatIQD(footer.total_delivery_price) : 0 }}</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--purple"><i class="material-icons">today</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('adminDashboard.ordersTodayLabel') }}</span>
              <span class="order-stat-value">{{ ordersTodayCount }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="panel-body"
      style="display: block;"
    >
      <div class="row">
        <preDatatableComponent
          ref="preDatatable"
          :hideReloadButton="true"
          :collapsibleFilters="true"
          :filtersVisible="showFilters"
          :hideBranch="true"
          :removeAddButton="true"
          :removeWarehouseDeliverButton="true"
          :tableData="tableData"
          :resetGetResults="resetGetResults"
          :resetGetResults2="resetGetResults2"
          :searchMe="searchMe"
          :enableCustomerSearchSuggestions="true"
          :pagination="pagination"
          :perPageFix="perPageFix"
          :filterTable="filterTable"
          :columns="columns"
          :showHideColumn="showHideColumn"
          :storageKey="'cols_orderDeliveries_v5'"
          :defaultHideCol="defaultHideCol"
          :resultsVersion="resultsVersion"
          :filterMade="filterMade"
          :totalResult="totalResult"
          :statusFilterItems="statusList"
          :statusFilterChange="handleStatusFilter"
          :defaultStatusFilterValues="defaultStatusFilterValues"
          :noWrapFilters="true"
        >
          <template v-slot:custom-filter-before-date>
            <template v-if="showFilters">
            <v-flex
              class="filter-field px-2"
              style="align-self: flex-end;"
            >
              <v-select
                :items="dateFieldFilterItems"
                v-model="tableData.date_field"
                @change="getResults()"
                :label="$t('columns.dateFilterByLabel')"
                single-line
                hide-details
                item-text="text"
                item-value="value"
              ></v-select>
            </v-flex>
            </template>
          </template>
        </preDatatableComponent>

        <v-dialog
          v-model="addModalOpen"
          max-width="480"
        >
          <v-card>
            <v-card-title>
              <div class="headline">{{ $t('delivery.requestDeliveryTitle') }}</div>
            </v-card-title>
            <v-card-text style="padding-top:12px; overflow:visible;">
              <v-select
                autocomplete
                :items="customers"
                item-text="text"
                item-value="value"
                v-model="selectedCustomerId"
                :filter="filterCustomers"
                :label="$t('delivery.customerRequiredLabel')"
                single-line
              />
              <v-text-field
                :label="$t('delivery.noteOptionalLabel')"
                v-model="newDeliveryNote"
                class="mt-2"
              />
            </v-card-text>
            <v-card-actions style="padding-bottom:12px;">
              <v-spacer></v-spacer>
              <v-btn
                @click="addModalOpen = false"
                outline
                style="padding: 0 16px !important;"
              >{{ $t('common.cancel') }}</v-btn>
              <v-btn
                color="primary"
                outline
                :disabled="!selectedCustomerId"
                @click="saveNewOrderDelivery()"
                style="padding: 0 16px !important;"
              >{{ $t('common.save') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog
          v-model="changeStatusDialog"
          max-width="480"
        >
          <v-card>
            <v-card-title>
              <div class="headline">{{ $t('delivery.changeStatusTitle') }}</div>
              <div
                v-if="changeStatusBulkMode"
                style="font-size:13px; color:#666; padding-top:2px;"
              >
                <strong>{{ selectedIds.length }}</strong> {{ $t('delivery.requestsSelectedSuffix') }}
              </div>
              <div
                v-else-if="changeStatusRow"
                style="font-size:13px; color:#666; padding-top:2px;"
              >
                {{ $t('delivery.invoiceColonPrefix') }} <strong>{{ changeStatusRow.invoice_number }}</strong>
              </div>
            </v-card-title>
            <v-card-text style="padding-top:12px; overflow:visible;">
              <v-select
                v-model="selectedNewStatus"
                :items="statusList"
                item-text="text"
                item-value="value"
                :label="$t('delivery.selectStatusLabel')"
                hide-details
              />
            </v-card-text>
            <v-card-actions style="padding-bottom:12px;">
              <v-spacer></v-spacer>
              <v-btn
                @click="changeStatusDialog = false"
                outline
                style="padding: 0 16px !important;"
              >{{ $t('common.cancel') }}</v-btn>
              <v-btn
                color="primary"
                outline
                :disabled="!selectedNewStatus || changeStatusSaving"
                @click="saveStatusChange()"
                style="padding: 0 16px !important;"
              >{{ $t('common.save') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <div class="col-xs-12">
          <div class="table-responsive oc-table-shell">
          <datatable
            v-if="!isMobile"
            :sortDir="tableData.sortDir"
            :columns="columns"
            :sortByColumn="sortByColumn"
            :searchOneCol="searchOneCol"
            :sortKey="sortKey"
            :sortOrders="sortOrders"
            :checkAll="checkAll"
            :unCheckAll="unCheckAll"
          >
            <tbody>
              <tr
                v-for="r in apiResults"
                :key="r.order_delivery_id"
              >
                <td>
                  <input
                    type="checkbox"
                    :value="r.order_delivery_id"
                    v-model="selectedIds"
                  />
                </td>
                <td class="oc-cell">
                  <div class="oc-order-number">{{ r.invoice_number }}</div>
                  <div class="oc-order-sub" v-if="r.attached_invoice_numbers">{{ $t('columns.attachedInvoices') }}: {{ r.attached_invoice_numbers }}</div>
                </td>
                <td class="oc-cell ws-nowrap">
                  <span class="customer-name-stack">
                    <span>{{ r.customer_name }}</span>
                    <span v-if="r.customer_phone" class="customer-phone-line">{{ r.customer_phone }}</span>
                    <span v-if="r.customer_phone2" class="customer-phone-line">{{ r.customer_phone2 }}</span>
                  </span>
                  <open-whatsapp-and-viber :phoneNumber="r.customer_phone" />
                  <div class="oc-item-sub customer-note-color" v-if="r.customer_note">{{ r.customer_note }}</div>
                </td>
                <td class="oc-cell" style="max-width: 220px; white-space: normal; word-break: break-word;">
                  <div class="oc-item-sub" v-if="profileAddress(r)">{{ $t('columns.address') }}: {{ profileAddress(r) }}</div>
                  <div class="oc-item-sub" v-if="defaultLocationAddress(r)">{{ $t('columns.defaultLocation') }}: {{ defaultLocationAddress(r) }}</div>
                </td>
                <td class="oc-cell">
                  <div class="oc-item-name" v-if="r.product_name">{{ r.product_name }}</div>
                  <span v-else>—</span>
                </td>
                <td class="combined-cell-td oc-cell">
                  <div class="combined-cell">
                    <div><span class="combined-cell-label">{{ $t('columns.totalPrice') }}:</span> $ {{ Number(r.total_price || 0).toFixed(2) }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.deliveryPrice') }}:</span> {{ formatIQD(r.delivery_price) }}</div>
                  </div>
                </td>
                <td class="oc-cell">
                  <span
                    class="oc-status-chip"
                    :style="{ background: statusColor(r.status).bg, color: statusColor(r.status).color, cursor: 'pointer' }"
                    :tooltip="$t('delivery.changeStatusTooltip')"
                    @click="openSingleChangeStatusDialog(r)"
                  >{{ statusLabel(r.status) }}</span>
                </td>
                <td class="oc-cell ws-nowrap">
                  <span class="ovd-date-stack">
                    <span class="ovd-date-line">{{ r.created_at | moment('YYYY-MM-DD') }}</span>
                    <span class="ovd-time-line">{{ r.created_at | moment('hh:mm A') }}</span>
                  </span>
                  <div class="oc-item-sub" v-if="r.posted_at">{{ $t('delivery.postStatus') }}: {{ formatStatusDate(r.posted_at) }}</div>
                  <div class="oc-item-sub" v-if="r.sent_at">{{ $t('orders.sentStatusTitle') }}: {{ formatStatusDate(r.sent_at) }}</div>
                  <div class="oc-item-sub" v-if="r.received_at">{{ $t('delivery.completeStatus') }}: {{ formatStatusDate(r.received_at) }}</div>
                  <div class="oc-item-sub" v-if="r.canceled_at">{{ $t('orders.cancelTooltip') }}: {{ formatStatusDate(r.canceled_at) }}</div>
                  <div class="oc-item-sub" v-if="r.held_at">{{ $t('delivery.holdStatus') }}: {{ formatStatusDate(r.held_at) }}</div>
                </td>
                <td class="oc-cell">{{ r.note || '—' }}</td>
                <td class="oc-cell oc-actions-cell">
                  <div class="oc-actions-grid">
                    <a
                      :tooltip="$t('delivery.viewInvoiceTooltip')"
                      class="oc-action-btn"
                      @click="initDetail('orderDeliveryModal', r.order_delivery_id)"
                    >
                      <i class="material-icons" style="color:#424242">assignment</i>
                    </a>

                    <a
                      v-if="$_has_perm('view_order_delivery_customer_orders')"
                      :tooltip="$t('delivery.viewCustomerOrdersImagesTooltip')"
                      class="oc-action-btn"
                      @click="viewCustomerOrders(r)"
                    >
                      <i class="material-icons" style="color:#424242">photo_library</i>
                    </a>

                    <a
                      v-if="$_has_perm('edit_order_delivery_price') && r.status != 4 && r.status != 2"
                      :tooltip="$t('delivery.editDeliveryPriceTooltip')"
                      class="oc-action-btn"
                      @click="editDeliveryPrice(r)"
                    >
                      <i
                        class="fa fa-pencil"
                        style="font-size: 18px"
                      />
                    </a>

                    <a
                      v-if="$_has_perm('delete_order_delivery') && (r.status === 1 || r.status === 2)"
                      :tooltip="$t('common.delete')"
                      class="oc-action-btn"
                      @click="deleteDelivery(r.order_delivery_id)"
                    >
                      <i
                        class="fa fa-trash red--text"
                        style="font-size: 20px"
                      />
                    </a>
                  </div>
                </td>
              </tr>
              <tr id="no-result-row">
                <td
                  :colspan="columns.length"
                  class="text-center"
                >
                  <v-alert
                    type="error"
                    :value="alertValue"
                  >
                    {{ $t('common.noResultsFound') }}
                  </v-alert>
                </td>
              </tr>
            </tbody>
          </datatable>
          <!-- Totals live outside the <table> on purpose: as a <tfoot>/<td colspan> they
               still fed the table's column-width algorithm and stretched the first column.
               A plain flex-wrap div below the table has zero effect on column widths -- same
               pattern as orderInvoice/orderInvoices.vue and deliveryInvoice/deliveryInvoices.vue. -->
          <div
            v-if="footer && !isMobile"
            class="oc-footer-totals"
          >
            <span style="color: #2e7d32;">{{ $t('delivery.totalPriceFooter', { amount: '$ ' + Number(footer.total_price || 0).toFixed(2) }) }}</span>
            <span style="color: #2e7d32;">{{ $t('delivery.totalDeliveryPriceFooter', { amount: formatIQD(footer.total_delivery_price) }) }}</span>
          </div>
          </div>

          <div v-if="isMobile" class="moc-container">
            <div
              v-if="footer"
              class="moc-card"
              style="margin-bottom: 12px; padding: 10px 12px; display: flex; justify-content: space-between; font-size: 13px; font-weight: 600; color: #2e7d32;"
            >
              <span>{{ $t('delivery.totalPriceFooter', { amount: '$ ' + Number(footer.total_price || 0).toFixed(2) }) }}</span>
              <span>{{ $t('delivery.deliveryPriceFooter', { amount: formatIQD(footer.total_delivery_price) }) }}</span>
            </div>

            <div
              v-for="r in apiResults"
              :key="r.order_delivery_id"
              class="moc-card"
            >
              <div class="moc-header">
                <input
                  type="checkbox"
                  :value="r.order_delivery_id"
                  v-model="selectedIds"
                />
                <span class="moc-item-code">{{ r.invoice_number }}</span>
                <span
                  class="moc-status-badge"
                  :style="{ backgroundColor: statusColor(r.status).bg, color: statusColor(r.status).color }"
                  :tooltip="$t('delivery.changeStatusTooltip')"
                  @click="openSingleChangeStatusDialog(r)"
                >{{ statusLabel(r.status) }}</span>
              </div>
              <div class="moc-body">
                <div class="moc-row" v-if="r.attached_invoice_numbers">
                  <span class="moc-label">{{ $t('sidebar.invoices') }}</span>
                  <span class="moc-value">{{ r.attached_invoice_numbers }}</span>
                </div>
                <div class="moc-row">
                  <span class="moc-label">{{ $t('columns.customer') }}</span>
                  <span class="moc-value">{{ r.customer_name }}</span>
                </div>
                <div class="moc-row" v-if="r.customer_note">
                  <span class="moc-label">{{ $t('delivery.custNoteColumn') }}</span>
                  <span class="moc-value">{{ r.customer_note }}</span>
                </div>
                <div class="moc-row--2col">
                  <div><span class="moc-label">{{ $t('columns.phone') }}</span><span class="moc-value">{{ r.customer_phone || '—' }}</span></div>
                  <div><span class="moc-label">{{ $t('delivery.phone2Label') }}</span><span class="moc-value">{{ r.customer_phone2 || '—' }}</span></div>
                </div>
                <div class="moc-row" v-if="profileAddress(r)">
                  <span class="moc-label">{{ $t('columns.address') }}</span>
                  <span class="moc-value">{{ profileAddress(r) }}</span>
                </div>
                <div class="moc-row" v-if="defaultLocationAddress(r)">
                  <span class="moc-label">{{ $t('columns.defaultLocation') }}</span>
                  <span class="moc-value">{{ defaultLocationAddress(r) }}</span>
                </div>
                <div class="moc-row" v-if="r.product_name">
                  <span class="moc-label">{{ $t('delivery.productColumn') }}</span>
                  <span class="moc-value">{{ r.product_name }}</span>
                </div>
                <div class="moc-row--2col">
                  <div><span class="moc-label">{{ $t('columns.totalPrice') }}</span><span class="moc-value">$ {{ Number(r.total_price || 0).toFixed(2) }}</span></div>
                  <div><span class="moc-label">{{ $t('columns.deliveryPrice') }}</span><span class="moc-value">{{ formatIQD(r.delivery_price) }} IQD</span></div>
                </div>
                <div class="moc-row" v-if="r.note">
                  <span class="moc-label">{{ $t('columns.note') }}</span>
                  <span class="moc-value">{{ r.note }}</span>
                </div>
                <div class="moc-row">
                  <span class="moc-label">{{ $t('delivery.createdColumn') }}</span>
                  <span class="moc-value">{{ r.created_at | moment('YYYY-MM-DD hh:mm a') }}</span>
                </div>
              </div>
              <div class="moc-actions">
                <a
                  style="color:#424242"
                  :tooltip="$t('delivery.viewInvoiceTooltip')"
                  @click="initDetail('orderDeliveryModal', r.order_delivery_id)"
                >
                  <i class="material-icons">assignment</i>
                </a>

                <a
                  v-if="$_has_perm('view_order_delivery_customer_orders')"
                  style="color:#424242"
                  :tooltip="$t('delivery.viewCustomerOrdersImagesTooltip')"
                  @click="viewCustomerOrders(r)"
                >
                  <i class="material-icons">photo_library</i>
                </a>

                <a
                  v-if="$_has_perm('edit_order_delivery_price') && r.status != 4 && r.status != 2"
                  :tooltip="$t('delivery.editDeliveryPriceTooltip')"
                  @click="editDeliveryPrice(r)"
                >
                  <i
                    class="fa fa-pencil"
                    style="font-size: 18px"
                  />
                </a>

                <a
                  v-if="$_has_perm('delete_order_delivery') && (r.status === 1 || r.status === 2)"
                  :tooltip="$t('common.delete')"
                  @click="deleteDelivery(r.order_delivery_id)"
                >
                  <i
                    class="fa fa-trash red--text"
                    style="font-size: 20px"
                  />
                </a>
              </div>
            </div>
            <div
              v-if="alertValue"
              style="text-align:center; padding:16px;"
            >
              <v-alert
                type="error"
                :value="alertValue"
              >{{ $t('common.noResultsFound') }}</v-alert>
            </div>
          </div>

          <order-detail-invoice
            :viewData="viewData"
            :closeModal="closeModal"
            :companyData="companyData"
            @detached="onInvoiceDetached"
          />

          <order-delivery-customer-images
            :viewData="customerOrdersViewData"
            :closeModal="closeModal"
          />

          <div class="row">
            <div class="col-xs-12 text-center oc-pagination">
              <paginate
                :page-count="pagination.lastPage"
                :page-range="2"
                :changePage="changePage"
                :container-class="'pagination'"
                :pagination="pagination"
                :page-class="'page-item'"
              >
              </paginate>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationVue from '../Pagination.vue'
import preDatatableComponent from '../preDatatableComponent.vue'
import DatatableVue from '../Datatable.vue'
import axios from 'axios'
import swal from 'sweetalert'
import orderDetailInvoice from './orderDeliveryInvoice.vue'
import orderDeliveryCustomerImages from './orderDeliveryCustomerImages.vue'
import toolbarButtonDisplay from '../../mixins/toolbarButtonDisplay'

export default {
  mixins: [toolbarButtonDisplay],
  components: {
    preDatatableComponent: preDatatableComponent,
    datatable: DatatableVue,
    paginate: PaginationVue,
    orderDetailInvoice,
    orderDeliveryCustomerImages
  },

  mounted() {
    this.perPageFix()
    this.getCompanyData()

    if (screen.width <= 600) {
      this.isMobile = true
    } else {
      this.isMobile = false
    }
  },

  $_veeValidate: {
    validator: 'new'
  },

  data() {
    let sortOrders = {}
    // Grouped columns (redesign, mirrors allOrder.vue's pattern) -- was a flat 20-column
    // list (21 <td>s including Actions, which never had its own header); each grouped cell
    // stacks several related fields (attached invoice numbers into Order Info; note/phones
    // into Customer; both addresses into Address; total/delivery price into Pricing; the 5
    // lifecycle timestamps into Date; Actions now gets its own header). Per-column hide no
    // longer makes sense at this granularity, so defaultHideCol is emptied and storageKey
    // bumped (v4 -> v5) so stale hidden-column selections referencing old positions aren't
    // silently misapplied.
    let columns = [
      { label: '', text: this.$t('columns.checkBoxColumn'), value: '1', sortable: false, searchable: false, name: '' },
      { label: this.$t('columns.orderInfo'), text: this.$t('columns.orderInfo'), value: '2', sortable: true, searchable: true, name: 'invoice_number' },
      { label: this.$t('columns.customer'), text: this.$t('columns.customer'), value: '3', sortable: true, searchable: true, name: 'customer_name' },
      { label: this.$t('columns.address'), text: this.$t('columns.address'), value: '4', sortable: false, searchable: false, name: 'address' },
      { label: this.$t('columns.productName'), text: this.$t('columns.productName'), value: '5', sortable: true, searchable: true, name: 'product_name' },
      { label: this.$t('columns.pricing'), text: this.$t('columns.pricing'), value: '6', sortable: false, searchable: false, name: 'total_price' },
      { label: this.$t('columns.status'), text: this.$t('columns.status'), value: '7', sortable: true, searchable: false, name: 'status_name' },
      { label: this.$t('common.date'), text: this.$t('common.date'), value: '8', sortable: true, searchable: true, name: 'created_at' },
      { label: this.$t('columns.note'), text: this.$t('columns.note'), value: '9', sortable: true, searchable: true, name: 'note' },
      { label: this.$t('columns.actions'), text: this.$t('columns.actions'), value: '10', sortable: false, searchable: false, name: 'actions' }
    ]
    columns.forEach(column => {
      sortOrders[column.name] = -1
    })

    return {
      url: 'api/order-delivery',
      isMobile: false,
      alertValue: false,
      filterMade: false,
      totalResult: 0,
      viewData: {
        order: {
          customer: {}
        },
        resolved_customer: {},
        order_invoices: [],
        direct_orders: []
      },
      customerOrdersViewData: {
        data: [],
        customer_name: ''
      },
      companyData: {},
      prevSearchQuery: '',
      apiResults: [],
      resultsVersion: 0,
      footer: null,
      formData: {},
      addModalOpen: false,
      selectedCustomerId: null,
      newDeliveryNote: '',
      customers: [],
      selectedIds: [],
      changeStatusDialog: false,
      changeStatusBulkMode: false,
      changeStatusRow: null,
      selectedNewStatus: null,
      changeStatusSaving: false,
      columns: columns,
      statusList: [
        { text: this.$t('orders.pendingStatus'), value: 1 },
        { text: this.$t('delivery.postStatus'), value: 5 },
        { text: this.$t('orders.sentStatusTitle'), value: 3 },
        { text: this.$t('delivery.completeStatus'), value: 4 },
        { text: this.$t('orders.cancelTooltip'), value: 2 },
        { text: this.$t('delivery.holdStatus'), value: 6 }
      ],
      dateFieldFilterItems: [
        { text: this.$t('columns.createdAt'), value: 'created_at' },
        { text: this.$t('delivery.completeStatus'), value: 'received_at' },
        { text: this.$t('orders.sentStatusTitle'), value: 'sent_at' },
        { text: this.$t('delivery.postStatus'), value: 'posted_at' },
        { text: this.$t('orders.cancelTooltip'), value: 'canceled_at' },
        { text: this.$t('delivery.holdStatus'), value: 'held_at' }
      ],
      // Grouped cells can't be hidden field-by-field any more -- same tradeoff allOrder.vue made.
      defaultHideCol: [],
      // Hidden by default (per-page preference, same localStorage convention as allOrder.vue).
      showFilters: localStorage.getItem('filtersVisible_orderDeliveries') === 'true',
      sortKey: 'order_delivery_id',
      sortOrders: sortOrders,
      // Pending, Post, Sent -- default status filter so the list opens showing only
      // active/in-progress delivery requests rather than the full unfiltered history.
      defaultStatusFilterValues: [1, 5, 3],
      tableData: {
        per_page: 10,
        search: '',
        column: 'order_delivery_id',
        sortDir: 'desc',
        searchOneCol: '',
        searchOneVal: '',
        from_date: '',
        to_date: '',
        date_field: 'created_at',
        statuses: [1, 5, 3]
      },
      pagination: {
        lastPage: 1,
        currentPage: 1,
        total: 1,
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: ''
      }
    }
  },

  computed: {
    // Best-effort only: counts within the currently loaded page of apiResults, not a true
    // system-wide count -- hence the "(current page)" note shown alongside it in the stat card.
    ordersTodayCount() {
      const today = new Date().toISOString().split('T')[0]
      return this.apiResults.filter(r => {
        const d = r.created_at
        return d && d.substr(0, 10) === today
      }).length
    }
  },

  methods: {
    toggleFiltersVisible() {
      this.showFilters = !this.showFilters
      localStorage.setItem('filtersVisible_orderDeliveries', this.showFilters ? 'true' : 'false')
    },

    // Replicates preDatatableComponent.vue's own built-in Reload button (now relocated
    // into the panel-heading here) -- reaches into the child's own filter-chip/date-range
    // reset state via $refs since that state lives inside the shared component, not here.
    reloadTable() {
      this.resetGetResults()
      const preDatatable = this.$refs.preDatatable
      if (preDatatable) {
        preDatatable.filterApplied = false
        preDatatable.resetDatePickerDefaults()
        preDatatable.ware = 'Select'
      }
    },

    // Scoped to #datatable and deterministic set/unset (rather than toggle-diffing) so
    // preDatatableComponent.vue's resultsVersion watcher can safely re-apply this after every
    // getResults() reload without columns flipping back to visible on the second call — see
    // allOrder.vue's showHideColumn for the same fix.
    showHideColumn(prevColVal, currentColVal) {
      const hidden = {}
      currentColVal.forEach((v) => { hidden[v] = true })

      for (let i = 1; i <= this.columns.length; i++) {
        const selector = '#datatable td:nth-child(' + i + '),#datatable th:nth-child(' + i + ')'
        if (hidden[i]) {
          $(selector).hide()
        } else {
          $(selector).show()
        }
      }
    },

    closeModal(modal) {
      let theModal = $('#' + modal)
      theModal.modal('toggle')
    },

    getCompanyData() {
      let token = localStorage.getItem('access_token')
      axios
        .get('api/company_info', { headers: { Authorization: 'Bearer ' + token } })
        .then(response => {
          this.companyData = response.data.data
        })
    },

    modal(modal) {
      $('#' + modal).modal({ backdrop: 'static' })
    },

    initDetail(modal, id) {
      let token = localStorage.getItem('access_token')

      axios
        .get(`api/order-delivery/${id}`, { headers: { Authorization: 'Bearer ' + token } })
        .then(res => {
          this.viewData = res.data
          this.modal(modal)
        })
    },

    // Every order belonging to this delivery request's customer (not just the ones attached
    // to this request), each with its shipping invoice (if any) and images.
    viewCustomerOrders(r) {
      let token = localStorage.getItem('access_token')

      axios
        .get(`api/order-delivery/${r.order_delivery_id}/customer-orders`, { headers: { Authorization: 'Bearer ' + token } })
        .then(res => {
          this.customerOrdersViewData = res.data
          this.modal('customerOrdersImagesModal')
        })
    },

    // Refreshes both the still-open View modal (its attached-invoices table and totals) and
    // the list's Attached Invoices column, since detaching changes both.
    onInvoiceDetached() {
      this.initDetail('orderDeliveryModal', this.viewData.order_delivery_id)
      this.getResults()
    },

    deleteDelivery(id) {
      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('delivery.deliveryRequestDeleteConfirm'),
        icon: 'warning',
        dangerMode: true,
        buttons: true
      }).then(willDelete => {
        if (willDelete) {
          let token = localStorage.getItem('access_token')
          axios
            .delete(`api/order-delivery/${id}`, { headers: { Authorization: 'Bearer ' + token } })
            .then(() => {
              this.getResults()
              swal({
                title: this.$t('delivery.deletedTitle'),
                text: '',
                icon: 'success',
                timer: 1500,
                buttons: false
              })
            })
        }
      })
    },

    formatIQD(value) {
      return Math.round(Number(value) || 0)
    },

    formatStatusDate(date) {
      if (!date) return '—'
      return this.$moment(date).format('YYYY-MM-DD hh:mm a')
    },

    // Same formatting convention as ViewCustomerDeliveryInvoice.vue/prepareToPrint.vue —
    // the customer's selected/default mobile delivery address, joined from its parts.
    formattedAddress(r) {
      const parts = [r.address_label, r.address_area, r.address_street, r.address_building_name, r.address_additional_directions].filter(Boolean)
      return parts.length > 0 ? parts.join(', ') : (r.address_name || '')
    },

    // The customer's profile address (governorate/city/address text entered when the
    // customer was created/edited) — independent of whatever mobile default location
    // they may or may not have selected.
    profileAddress(r) {
      const parts = [r.profile_governorate, r.profile_city, r.profile_address].filter(Boolean)
      return parts.join(', ')
    },

    // The customer's currently selected default mobile delivery location, falling back to
    // their profile address when no default location has been selected.
    defaultLocationAddress(r) {
      return this.formattedAddress(r) || this.profileAddress(r)
    },

    editDeliveryPrice(r) {
      // IQD has no fractional subunit in practice — default to a clean 5000 rather
      // than 0.00/5000.00 when the field hasn't been set yet.
      const defaultValue = Number(r.delivery_price) > 0 ? this.formatIQD(r.delivery_price) : 5000

      swal(this.$t('delivery.deliveryPriceIqdTitle'), {
        content: {
          element: 'input',
          attributes: {
            placeholder: this.$t('delivery.deliveryPricePlaceholder'),
            className: 'form-control',
            type: 'number',
            value: defaultValue
          }
        },
        buttons: [this.$t('common.cancel'), this.$t('common.save')]
      }).then(value => {
        // sweetalert resets its tracked value to '' when the dialog opens and only updates
        // it on user keystrokes — so clicking Save without touching the prefilled field
        // resolves as '' here, not the visible default. Null still means Cancel was clicked.
        if (value === null) return
        const price = value === '' ? defaultValue : value

        let token = localStorage.getItem('access_token')
        axios
          .put(`api/order-delivery/${r.order_delivery_id}`, { delivery_price: price }, { headers: { Authorization: 'Bearer ' + token } })
          .then(() => {
            this.getResults()
            swal({
              title: this.$t('common.done'),
              text: '',
              icon: 'success',
              timer: 1500,
              buttons: false
            })
          })
      })
    },

    checkAll() {
      this.selectedIds = this.apiResults.map(r => r.order_delivery_id)
    },

    unCheckAll() {
      this.selectedIds = []
    },

    handleStatusFilter(val) {
      this.tableData.statuses = val || []
      this.getResults()
    },

    statusColor(status) {
      const map = {
        1: { bg: '#E0E0E0', color: '#424242' }, // Pending
        2: { bg: '#F63049', color: '#FFFFFF' }, // Cancel
        3: { bg: '#42A5F5', color: '#FFFFFF' }, // Sent
        4: { bg: '#2E7D32', color: '#FFFFFF' }, // Complete
        5: { bg: '#FFC85C', color: '#6B4C00' }, // Post
        6: { bg: '#9C27B0', color: '#FFFFFF' } // Hold
      }
      return map[status] || { bg: '#E0E0E0', color: '#424242' }
    },

    // Same codes/keys as statusList above -- status_name from the API is always
    // English, so the badge label maps the numeric code through $t() instead.
    statusLabel(status) {
      const map = {
        1: this.$t('orders.pendingStatus'),
        2: this.$t('orders.cancelTooltip'),
        3: this.$t('orders.sentStatusTitle'),
        4: this.$t('delivery.completeStatus'),
        5: this.$t('delivery.postStatus'),
        6: this.$t('delivery.holdStatus')
      }
      return map[status] || ''
    },

    openBulkChangeStatusDialog() {
      if (this.selectedIds.length === 0) {
        swal({ text: this.$t('delivery.pleaseSelectAtLeastOneDeliveryRequest'), icon: 'warning' })
        return
      }
      this.changeStatusBulkMode = true
      this.changeStatusRow = null
      this.selectedNewStatus = null
      this.changeStatusDialog = true
    },

    openSingleChangeStatusDialog(r) {
      this.changeStatusBulkMode = false
      this.changeStatusRow = r
      this.selectedNewStatus = r.status
      this.changeStatusDialog = true
    },

    saveStatusChange() {
      if (!this.selectedNewStatus) return

      const ids = this.changeStatusBulkMode ? this.selectedIds : [this.changeStatusRow.order_delivery_id]
      this.changeStatusSaving = true
      let token = localStorage.getItem('access_token')
      axios
        .post('api/order-delivery/change-status', {
          order_delivery_ids: ids,
          status: this.selectedNewStatus
        }, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.changeStatusSaving = false
          this.changeStatusDialog = false
          if (this.changeStatusBulkMode) this.unCheckAll()
          this.getResults()
          swal({ text: response.data.message, icon: 'success', timer: 1500, buttons: false })
        })
        .catch((err) => {
          this.changeStatusSaving = false
          const msg = (err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : 'Something went wrong!'
          swal({ text: msg, icon: 'error' })
        })
    },

    openAddModal() {
      this.selectedCustomerId = null
      this.newDeliveryNote = ''
      this.addModalOpen = true
      if (this.customers.length === 0) {
        this.fetchCustomerSearchList()
      }
    },

    fetchCustomerSearchList() {
      let token = localStorage.getItem('access_token')
      axios
        .get('api/get-customer-to-select', { headers: { Authorization: 'Bearer ' + token } })
        .then(response => {
          const list = response.data.data || []
          this.customers = list.map(c => {
            const phone1 = c.phone_number || ''
            const phone2 = c.phone_number_2 || ''
            const phones = [phone1, phone2].filter(Boolean).join(' / ')
            return {
              text: phones ? c.full_name + ' - Tel: ' + phones : c.full_name,
              value: c.user_id,
              full_name: c.full_name,
              phone_number: phone1,
              phone_number_2: phone2
            }
          })
        })
        .catch(() => { this.customers = [] })
    },

    filterCustomers(item, queryText, itemText) {
      const query = String(queryText || '').toLowerCase().trim()
      if (!query) return true

      const visibleFields = [
        itemText,
        item && item.full_name,
        item && item.phone_number,
        item && item.phone_number_2
      ]
        .filter(Boolean)
        .map(value => String(value).toLowerCase())

      if (visibleFields.some(value => value.includes(query))) {
        return true
      }

      const normalizedQuery = this.normalizePhoneSearch(query)
      if (!normalizedQuery) {
        return false
      }

      return [item && item.phone_number, item && item.phone_number_2]
        .filter(Boolean)
        .some(value => this.normalizePhoneSearch(value).includes(normalizedQuery))
    },

    normalizePhoneSearch(value) {
      return String(value || '').replace(/\D/g, '')
    },

    saveNewOrderDelivery() {
      if (!this.selectedCustomerId) return

      let token = localStorage.getItem('access_token')
      axios
        .post('api/order-delivery/admin-create', {
          customer_id: this.selectedCustomerId,
          note: this.newDeliveryNote
        }, { headers: { Authorization: 'Bearer ' + token } })
        .then(() => {
          this.addModalOpen = false
          this.getResults()
          swal({
            title: this.$t('common.done'),
            text: '',
            icon: 'success',
            timer: 1500,
            buttons: false
          })
        })
        .catch(err => {
          const msg = (err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : 'Something went wrong!'
          swal({ text: msg, icon: 'error' })
        })
    },

    getResults(url = this.url) {
      var token = localStorage.getItem('access_token')
      let theDatatable = document.querySelector('.the-datatable')
      let thePanel = document.querySelector('.panel-flat')

      // A selection made before a filter/search/reload must not silently carry over into
      // the new result set — otherwise a stale row (no longer even visible) stays selected
      // and gets included in the next bulk Change Status, changing rows the user never
      // intended to touch in the current (filtered) view.
      this.selectedIds = []

      if (theDatatable != null) {
        theDatatable.classList.add('slide-table-out')
        thePanel.style.overflowX = 'auto'
      }

      axios
        .get(url, { params: this.tableData, headers: { Authorization: 'Bearer ' + token } })
        .then(response => {
          let data = response.data
          const isFlatArray = Array.isArray(data.data)
          if (isFlatArray) {
            this.filterMade = true
            this.apiResults = data.data
          } else {
            this.filterMade = false
            this.apiResults = data.data.data
          }
          this.footer = data.footer || null
          this.resultsVersion++

          if (this.apiResults.length === 0) {
            this.tableData.searchOneCol = ''
            this.tableData.searchOneVal = ''
            this.tableData.search = ''
            this.alertValue = true
          } else {
            this.alertValue = false
          }
          if (isFlatArray) {
            this.removeConfigPagination()
          } else {
            this.configPagination(data.data)
          }
          theDatatable.classList.remove('slide-table-out')
          setTimeout(function () {
            thePanel.style.overflowX = 'auto'
          }, 0)
        })
        .then(() => {
          this.totalResult = 0
          for (let i = 0; i < this.apiResults.length; i++) {
            this.totalResult += 1
          }
        })
        .catch(errors => { console.error(errors) })
    },

    removeConfigPagination() {
      this.pagination.lastPage = ''
      this.pagination.currentPage = ''
      this.pagination.total = ''
      this.pagination.lastPageUrl = ''
      this.pagination.nextPageUrl = ''
      this.pagination.prevPageUrl = ''
      this.pagination.from = ''
      this.pagination.to = ''
    },

    resetGetResults() {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      this.tableData.search = ''
      this.sortKey = 'order_delivery_id'
      this.tableData.sortDir = 'desc'
      this.tableData.column = 'order_delivery_id'
      this.tableData.from_date = ''
      this.tableData.to_date = ''
      this.tableData.date_field = 'created_at'
      this.getResults()
    },

    resetGetResults2() {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      this.tableData.search = ''
      this.sortKey = 'order_delivery_id'
      this.tableData.sortDir = 'desc'
      this.tableData.column = 'order_delivery_id'
      this.tableData.from_date = ''
      this.tableData.to_date = ''
      this.tableData.date_field = 'created_at'
      this.getResults()
    },

    perPageFix(perPage) {
      this.tableData.per_page = perPage
      this.getResults()
    },

    changePage(pageUrl) {
      this.url = pageUrl
      this.getResults()
    },

    searchMe(str) {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      if (str.trim() !== '') {
        this.tableData.search = str
        this.prevSearchQuery = str
        this.getResults()
      } else {
        swal({
          text: this.$t('common.typeToSearch'),
          icon: 'warning',
          dangerMode: true
        })
        if (this.prevSearchQuery !== '') {
          this.tableData.search = ''
          this.prevSearchQuery = ''
          this.getResults()
        }
      }
    },

    sortByColumn(str) {
      this.tableData.column = str
      this.tableData.sortKey = str
      this.tableData.sortDir = this.tableData.sortDir === 'desc' ? 'asc' : 'desc'
      this.getResults()
    },

    searchOneCol(columnName, str) {
      this.tableData.searchOneCol = columnName
      this.tableData.searchOneVal = str
      this.getResults()
    },

    filterTable(fromDate, toDate, dateField) {
      this.tableData.from_date = fromDate
      this.tableData.to_date = toDate
      this.tableData.date_field = dateField || this.tableData.date_field || 'created_at'
      this.getResults()
    },

    configPagination(data) {
      this.pagination.lastPage = data.last_page
      this.pagination.currentPage = data.current_page
      this.pagination.total = data.total
      this.pagination.lastPageUrl = data.last_page_url
      this.pagination.nextPageUrl = data.next_page_url
      this.pagination.prevPageUrl = data.prev_page_url
      this.pagination.from = data.from
      this.pagination.to = data.to
    }
  }
}
</script>

<style>
@media screen and (max-width: 600px) {
  th {
    font-weight: bold;
    color: #273246;
    padding-left: 5px !important;
  }

  td {
    padding-left: 5px !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}

.panel-heading h4 {
  text-transform: uppercase;
  margin-left: -20px;
  padding-left: 10px;
  font-weight: 500;
}

.col-xs-12 {
  padding-top: 10px;
  padding-bottom: 10px;
}
.overlay--active:before {
  opacity: 50 !important;
  background: rgba(0, 0, 0, 0.5) !important;
}

.dialog {
  box-shadow: 0 3px 5px -1px rgba(14, 14, 14, 0.212) !important;
  overflow: hidden;
  overflow-y: hidden;
}
.page-stats {
  align-items: center;
  position: relative;
  top: -8px;
  margin-right: 5px;
}

.has-error .select2-selection--single:not([class*='bg-']):not([class*='border-']) {
  border-bottom-color: red;
}

a:hover {
  color: #03a9f4;
  position: relative;
}
a[tooltip]:hover:after {
  content: attr(tooltip);
  padding: 8px 12px;
  color: #ffffff;
  position: absolute;
  right: 80%;
  bottom: 150%;
  white-space: nowrap;
  z-index: 9999;
  -moz-border-radius: 3px;
  -webkit-border-radius: 3px;
  border-radius: 3px;
  -moz-box-shadow: 0px 0px 2px #484b4e;
  -webkit-box-shadow: 0px 0px 2px #484b4e;
  box-shadow: 0px 0px 2px #484b4e;
  background-color: #484b4e;
}

@media screen and (max-width: 767px) {
  .form-group {
    margin-top: 0;
    margin-bottom: 0;
  }
}

.status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.customer-name-stack {
  display: inline-flex;
  flex-direction: column;
  vertical-align: middle;
}
.customer-phone-line {
  display: block;
  font-size: 14px;
  color: #666;
}
.combined-cell-td {
  vertical-align: middle;
}
.combined-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  font-size: 15px;
  line-height: 1.4;
  white-space: nowrap;
}
.combined-cell-label {
  color: #888;
}

/* Totals strip -- rendered as a div AFTER </datatable>, not a <tfoot>, so it has no say in
   the table's column widths (a <td colspan> still does, and was stretching the first column). */
.oc-footer-totals {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 20px;
  padding: 10px 12px;
  font-weight: bold;
  border-top: 1px solid #eee;
}
.oc-footer-totals > span {
  white-space: nowrap;
}

</style>

<style scoped>
/* Allow the toolbar filter row to compress/wrap when extra controls are present
   (like Date Filter By) so it doesn't force page-level horizontal scroll. */
>>> .filters-no-wrap {
  overflow-x: visible !important;
  flex-wrap: wrap !important;
}
>>> .filters-no-wrap > .filter-field {
  flex-shrink: 1 !important;
  min-width: 96px !important;
  flex-basis: 96px !important;
}
>>> .filters-no-wrap > .filter-field-wide {
  flex-shrink: 1 !important;
  min-width: 124px !important;
  flex-basis: 124px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/orderDelivery/orderDeliveries.vue